import React from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {RootState} from '../../store';
import styles from './home/Home.module.css';
import HomeHero from './home/components/HomeHero';
import ProUpgradeBanner from './home/components/ProUpgradeBanner';
import LiveNowSection from './home/components/LiveNowSection';
import LiveReelsSection from './home/components/LiveReelsSection';
import PostsFeedSection from './home/components/PostsFeedSection';
import HomeSidebar from './home/components/HomeSidebar';
import CreatePostModal from './home/components/CreatePostModal';
import RepostPostModal from './home/components/RepostPostModal';
import UploadReelModal from './home/components/UploadReelModal';
import ReelPlayerModal from './home/components/ReelPlayerModal';
import ShareReelToCoachModal from './home/components/ShareReelToCoachModal';
import CommentsModal from './home/components/CommentsModal';
import LiveBroadcastModal from './home/components/LiveBroadcastModal';
import LiveViewerModal from './home/components/LiveViewerModal';
import {useHomeHeaderTheme} from './home/hooks/useHomeHeaderTheme';
import {useHomeStats} from './home/hooks/useHomeStats';
import {useHomeTrendingTopics} from './home/hooks/useHomeTrendingTopics';
import {useHomeSuggestions} from './home/hooks/useHomeSuggestions';
import {useHomePosts} from './home/hooks/useHomePosts';
import {useHomeLiveSessions} from './home/hooks/useHomeLiveSessions';
import {useHomeLiveBroadcast} from './home/hooks/useHomeLiveBroadcast';
import {useHomeLiveViewer} from './home/hooks/useHomeLiveViewer';
import {useHomeReels} from './home/hooks/useHomeReels';
import {useHomeComments} from './home/hooks/useHomeComments';
import {useCreatePostComposer} from './home/hooks/useCreatePostComposer';
import {useRepostComposer} from './home/hooks/useRepostComposer';
import {useUploadReelComposer} from './home/hooks/useUploadReelComposer';
import {HomeReel, socialFeedApi, TrendingTopic} from '../../services/socialFeedApi';
import {isProPlanTier} from '../../utils/plan';
import {getAuthorName} from './home/utils';
import {buildHomeTopicSearch, formatHomeTopicLabel, normalizeHomeTopicKey} from './home/topicFilter';

const getFocusedPostIdFromLocationState = (state: unknown) => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const focusPostId = (state as {focusPostId?: unknown}).focusPostId;

  return typeof focusPostId === 'string' && focusPostId.trim() ? focusPostId : null;
};

const formatCompactCount = (value: number) => new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: value >= 10000 ? 0 : 1,
}).format(value);

const formatExactCount = (value: number) => new Intl.NumberFormat('en-US').format(value);

const Home: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const isProUser = isProPlanTier(user?.planTier);
  const sharedPostId = React.useMemo(() => (
    new URLSearchParams(location.search).get('post') || getFocusedPostIdFromLocationState(location.state)
  ), [location.search, location.state]);
  const sharedReelId = React.useMemo(() => new URLSearchParams(location.search).get('reel'), [location.search]);
  const activeTopicKey = React.useMemo(() => {
    return normalizeHomeTopicKey(new URLSearchParams(location.search).get('topic'));
  }, [location.search]);
  const [shareReel, setShareReel] = React.useState<HomeReel | null>(null);
  const [sharedReelError, setSharedReelError] = React.useState<string | null>(null);

  const {backgroundStyle} = useHomeHeaderTheme(token);
  const {stats, statsLoading, statsError, adjustFollowingCount, fetchStats} = useHomeStats(token);
  const {
    suggestions,
    suggestionsLoading,
    suggestionsError,
    suggestionFollowError,
    pendingSuggestionFollowIds,
    retrySuggestions,
    dismissSuggestionFollowError,
    followSuggestion,
  } = useHomeSuggestions(token, adjustFollowingCount);
  const {
    topics: trendingTopics,
    loading: trendingTopicsLoading,
    error: trendingTopicsError,
    refresh: fetchTrendingTopics,
  } = useHomeTrendingTopics(token);
  const activeTopicLabel = React.useMemo(() => (
    trendingTopics.find((topic) => topic.key === activeTopicKey)?.label
    || (activeTopicKey ? formatHomeTopicLabel(activeTopicKey) : null)
    || null
  ), [activeTopicKey, trendingTopics]);
  const {
    liveSessions,
    liveSessionsLoading,
    liveSessionsError,
    fetchLiveSessions,
    upsertLiveSession,
    removeLiveSession,
  } = useHomeLiveSessions(token);
  const {
    posts,
    postsLoading,
    postsError,
    hasMorePosts,
    loadMorePostsLoading,
    loadMorePostsError,
    postActionError,
    failedImages,
    reactingPostIds,
    fetchPosts,
    loadMorePosts,
    handleImageError,
    handleSelectReaction,
    handleSharePost,
    highlightedPostId,
    prependPost,
    updatePostCommentCount,
    pendingFollowAuthorIds,
    handleToggleAuthorFollow,
    handleDeletePost,
    deletingPostIds,
  } = useHomePosts(token, sharedPostId, adjustFollowingCount, activeTopicKey);
  const {
    reels,
    reelsLoading,
    reelsError,
    selectedView,
    searchQuery,
    hasMoreReels,
    loadMoreReelsLoading,
    loadMoreReelsError,
    failedReelThumbnails,
    selectedReel,
    activeReelId,
    reelPlayerError,
    setSelectedView,
    setSearchQuery,
    setActiveReelId,
    refreshReels,
    resetReelsFeed,
    loadMoreReels,
    registerPendingReelThumbnail,
    handleReelThumbnailError,
    openReelPlayer,
    closeReelPlayer,
    handleSelectedReelPlay,
    handleSelectedReelLike,
    likingReelIds,
    setReelPlayerError,
    updateReelCommentCount,
  } = useHomeReels(token, activeTopicKey);

  const updateTopicFilter = React.useCallback((topic: TrendingTopic | null) => {
    navigate({
      pathname: location.pathname,
      search: buildHomeTopicSearch(location.search, topic?.key || null),
    });

    if (topic) {
      window.setTimeout(() => {
        document.getElementById('reels')?.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, 0);
    }
  }, [location.pathname, location.search, navigate]);
  const {
    selectedTarget,
    isCommentsModalOpen,
    comments,
    commentsLoading,
    commentsError,
    commentDraft,
    commentValidationError,
    commentSubmitError,
    submittingComment,
    deletingCommentIds,
    setCommentDraft,
    openComments,
    closeComments,
    retryLoadingComments,
    submitComment,
    deleteComment,
  } = useHomeComments({
    token,
    onPostCommentCountChange: updatePostCommentCount,
    onReelCommentCountChange: updateReelCommentCount,
  });
  const {
    isCreatePostModalOpen,
    newPostContent,
    selectedPostImageFiles,
    selectedPostImagePreviewUrls,
    createPostLoading,
    createPostError,
    createPostValidationError,
    createPostStatusMessage,
    createPostUploadProgressPercent,
    createPostTextareaRef,
    createPostFileInputRef,
    setNewPostContent,
    removeSelectedPostImage,
    closeCreatePostModal,
    openCreatePostModal,
    openCreatePostFileBrowser,
    handleSelectedPostImageChange,
    handleCreatePostSubmit,
  } = useCreatePostComposer(token, fetchPosts);
  const {
    isRepostModalOpen,
    repostTargetPost,
    repostMessage,
    repostLoading,
    repostError,
    repostValidationError,
    repostTextareaRef,
    setRepostMessage,
    openRepostModal,
    closeRepostModal,
    handleRepostSubmit,
  } = useRepostComposer(token, prependPost);
  const {
    isUploadReelModalOpen,
    newReelTitle,
    newReelDescription,
    newReelIsFeatured,
    selectedReelVideoFile,
    selectedReelVideoPreviewUrl,
    selectedReelVideoDurationSeconds,
    uploadReelMetadataLoading,
    uploadReelLoading,
    uploadReelError,
    uploadReelValidationError,
    uploadReelStatusMessage,
    uploadReelProgressPercent,
    uploadReelTitleInputRef,
    uploadReelFileInputRef,
    setNewReelTitle,
    setNewReelDescription,
    setNewReelIsFeatured,
    clearSelectedReelVideo,
    closeUploadReelModal,
    openUploadReelModal,
    openUploadReelFileBrowser,
    handleSelectedReelVideoChange,
    handleUploadReelSubmit,
  } = useUploadReelComposer(token, resetReelsFeed, registerPendingReelThumbnail);
  const refreshLiveSessions = React.useCallback(async () => {
    await fetchLiveSessions();
  }, [fetchLiveSessions]);
  const {
    isBroadcastModalOpen,
    broadcastSession,
    broadcastAccessToken,
    broadcastLiveKitUrl,
    broadcastPhase,
    broadcastError,
    openBroadcastModal,
    closeBroadcastModal,
    handleBroadcastConnected,
    handleBroadcastDisconnected,
    handleBroadcastError,
    handleBroadcastMediaDeviceFailure,
    handleBroadcastPreviewCapture,
    endBroadcast,
  } = useHomeLiveBroadcast({
    token,
    userName: user?.name,
    onSessionStarted: upsertLiveSession,
    onSessionEnded: removeLiveSession,
    refreshLiveSessions,
  });
  const {
    isViewerModalOpen,
    viewerSession,
    viewerAccessToken,
    viewerLiveKitUrl,
    viewerLoading,
    viewerError,
    openViewerModal,
    closeViewerModal,
    handleViewerConnected,
    handleViewerDisconnected,
    handleViewerError,
  } = useHomeLiveViewer({
    token,
    onSessionUpdated: upsertLiveSession,
    onSessionEnded: removeLiveSession,
    refreshLiveSessions,
  });

  const statsUnavailable = statsLoading || Boolean(statsError);

  const heroStats = [
    {label: 'Followers', value: statsUnavailable ? '--' : formatCompactCount(stats.followerCount)},
    {label: 'Following', value: statsUnavailable ? '--' : formatCompactCount(stats.followingCount)},
    {label: 'Posts', value: statsUnavailable ? '--' : formatCompactCount(stats.postCount)},
    {label: 'New Messages', value: statsUnavailable ? '--' : formatCompactCount(stats.newMessageCount), highlighted: true},
  ];

  const quickStats = [
    {label: 'Followers', value: statsUnavailable ? '--' : formatExactCount(stats.followerCount)},
    {label: 'Following', value: statsUnavailable ? '--' : formatExactCount(stats.followingCount)},
    {label: 'Posts', value: statsUnavailable ? '--' : formatExactCount(stats.postCount)},
    {label: 'Messages', value: statsUnavailable ? '--' : `${formatExactCount(stats.newMessageCount)} new`, highlighted: true},
  ];

  React.useEffect(() => {
    if (!token || !sharedReelId) {
      return;
    }

    let isMounted = true;

    const openSharedReel = async () => {
      try {
        setSharedReelError(null);
        const response = await socialFeedApi.getHighlightReel(token, sharedReelId);

        if (!isMounted) {
          return;
        }

        openReelPlayer(response.reel);
      } catch {
        if (isMounted) setSharedReelError('This reel is no longer available.');
      } finally {
        if (!isMounted) {
          return;
        }

        const nextSearchParams = new URLSearchParams(location.search);
        nextSearchParams.delete('reel');
        navigate({
          pathname: location.pathname,
          search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : '',
        }, {replace: true});
      }
    };

    void openSharedReel();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search, navigate, openReelPlayer, sharedReelId, token]);

  return (
    <div className={styles.pageRoot}>
      <HomeHero
        backgroundStyle={backgroundStyle}
        userName={user?.name}
        stats={heroStats}
        onCreatePost={openCreatePostModal}
        onGoLive={() => {
          void openBroadcastModal();
        }}
        onUploadReel={() => {
          if (isProUser) {
            openUploadReelModal();
            return;
          }

          navigate('/settings?upgrade=1');
        }}
        onDiscoverTalent={() => {
          navigate('/discover-talent');
        }}
        showGoLive={isProUser}
        reelActionLabel={isProUser ? 'Upload Reel' : 'Upgrade for Reels'}
      />

      {sharedReelError ? <div className={styles.sharedReelError} role="alert">{sharedReelError}</div> : null}

      {!isProUser ? <ProUpgradeBanner /> : null}

      <LiveNowSection
        sessions={liveSessions}
        sessionsLoading={liveSessionsLoading}
        sessionsError={liveSessionsError}
        onRetry={() => {
          void fetchLiveSessions();
        }}
        onStartLive={() => {
          if (isProUser) {
            void openBroadcastModal();
            return;
          }

          navigate('/settings?upgrade=1');
        }}
        onOpenSession={(session) => {
          void openViewerModal(session);
        }}
      />

      {activeTopicKey && activeTopicLabel ? (
        <div className={styles.topicFilterBar} role="status" aria-live="polite">
          <span>Showing topic: <strong>{activeTopicLabel}</strong></span>
          <button type="button" className={styles.topicFilterClearButton} onClick={() => updateTopicFilter(null)}>
            Clear
          </button>
        </div>
      ) : null}

      <LiveReelsSection
        reels={reels}
        reelsLoading={reelsLoading}
        reelsError={reelsError}
        selectedView={selectedView}
        searchQuery={searchQuery}
        hasMoreReels={hasMoreReels}
        loadMoreReelsLoading={loadMoreReelsLoading}
        loadMoreReelsError={loadMoreReelsError}
        activeReelId={activeReelId}
        failedReelThumbnails={failedReelThumbnails}
        onRetry={() => {
          void refreshReels();
        }}
        onActivateReel={setActiveReelId}
        onPlayReel={openReelPlayer}
        onThumbnailError={handleReelThumbnailError}
        onViewChange={setSelectedView}
        onSearchChange={setSearchQuery}
        onLoadMoreReels={() => {
          void loadMoreReels();
        }}
        onCreateReel={() => {
          if (isProUser) {
            openUploadReelModal();
            return;
          }

          navigate('/settings?upgrade=1');
        }}
        activeTopicLabel={activeTopicLabel}
      />

      <div id="recent-posts" className={styles.feedLayoutSection}>
        <div className={styles.feedLayoutInner}>
          <div className={styles.contentGrid}>
            <PostsFeedSection
              posts={posts}
              postsLoading={postsLoading}
              postsError={postsError}
              hasMorePosts={hasMorePosts}
              loadMorePostsLoading={loadMorePostsLoading}
              loadMorePostsError={loadMorePostsError}
              postActionError={postActionError}
              failedImages={failedImages}
              reactingPostIds={reactingPostIds}
              pendingFollowAuthorIds={pendingFollowAuthorIds}
              currentUserId={user?.id || null}
              deletingPostIds={deletingPostIds}
              onDeletePost={async post => {
                if (user?.id && await handleDeletePost(post, user.id)) {
                  void fetchStats();
                }
              }}
              onRetry={() => {
                void fetchPosts();
              }}
              onLoadMorePosts={() => {
                void loadMorePosts();
              }}
              onImageError={handleImageError}
              onSelectReaction={(postId, reactionType) => {
                void handleSelectReaction(postId, reactionType);
              }}
              onToggleAuthorFollow={(authorId, isFollowing) => {
                void handleToggleAuthorFollow(authorId, isFollowing);
              }}
              onSharePost={(post) => handleSharePost(post)}
              onOpenRepost={openRepostModal}
              onOpenComments={(post) => {
                openComments({
                  type: 'post',
                  id: post._id,
                  title: `${getAuthorName(post)}'s post`,
                  subtitle: 'Join the conversation',
                  commentCount: post.commentCount,
                });
              }}
              highlightedPostId={highlightedPostId}
              activeTopicLabel={activeTopicLabel}
            />
            <HomeSidebar
              quickStats={quickStats}
              trendingTopics={trendingTopics}
              trendingTopicsLoading={trendingTopicsLoading}
              trendingTopicsError={trendingTopicsError}
              activeTopicKey={activeTopicKey}
              onSelectTopic={updateTopicFilter}
              onRetryTrendingTopics={() => {
                void fetchTrendingTopics();
              }}
              suggestions={suggestions}
              suggestionsLoading={suggestionsLoading}
              suggestionsError={suggestionsError}
              suggestionFollowError={suggestionFollowError}
              pendingSuggestionFollowIds={pendingSuggestionFollowIds}
              onRetrySuggestions={retrySuggestions}
              onDismissSuggestionFollowError={dismissSuggestionFollowError}
              onFollowSuggestion={(userId) => {
                void followSuggestion(userId);
              }}
              onViewSuggestion={(userId) => {
                navigate(`/profile?userId=${encodeURIComponent(userId)}`);
              }}
            />
          </div>
        </div>
      </div>

      <LiveBroadcastModal
        isOpen={isBroadcastModalOpen}
        session={broadcastSession}
        accessToken={broadcastAccessToken}
        liveKitUrl={broadcastLiveKitUrl}
        phase={broadcastPhase}
        error={broadcastError}
        closeDisabled={broadcastPhase === 'live' || broadcastPhase === 'ending'}
        onClose={closeBroadcastModal}
        onEndLive={() => {
          void endBroadcast();
        }}
        onConnected={handleBroadcastConnected}
        onDisconnected={handleBroadcastDisconnected}
        onError={(error) => {
          handleBroadcastError(error.message || 'Failed to connect your live session');
        }}
        onMediaDeviceFailure={handleBroadcastMediaDeviceFailure}
        onPreviewCapture={handleBroadcastPreviewCapture}
      />

      <LiveViewerModal
        isOpen={isViewerModalOpen}
        session={viewerSession}
        accessToken={viewerAccessToken}
        liveKitUrl={viewerLiveKitUrl}
        loading={viewerLoading}
        error={viewerError}
        onClose={closeViewerModal}
        onConnected={handleViewerConnected}
        onDisconnected={handleViewerDisconnected}
        onError={handleViewerError}
      />

      <UploadReelModal
        isOpen={isProUser && isUploadReelModalOpen}
        loading={uploadReelLoading}
        metadataLoading={uploadReelMetadataLoading}
        error={uploadReelError}
        validationError={uploadReelValidationError}
        statusMessage={uploadReelStatusMessage}
        uploadProgressPercent={uploadReelProgressPercent}
        title={newReelTitle}
        description={newReelDescription}
        isFeatured={newReelIsFeatured}
        selectedVideoFile={selectedReelVideoFile}
        selectedVideoPreviewUrl={selectedReelVideoPreviewUrl}
        selectedVideoDurationSeconds={selectedReelVideoDurationSeconds}
        titleInputRef={uploadReelTitleInputRef}
        fileInputRef={uploadReelFileInputRef}
        onTitleChange={setNewReelTitle}
        onDescriptionChange={setNewReelDescription}
        onFeaturedChange={setNewReelIsFeatured}
        onClose={closeUploadReelModal}
        onBrowseVideo={openUploadReelFileBrowser}
        onRemoveVideo={clearSelectedReelVideo}
        onVideoChange={handleSelectedReelVideoChange}
        onSubmit={handleUploadReelSubmit}
      />

      <ReelPlayerModal
        reel={selectedReel}
        error={reelPlayerError}
        onClose={closeReelPlayer}
        onPlay={handleSelectedReelPlay}
        onToggleLike={handleSelectedReelLike}
        liking={Boolean(selectedReel && likingReelIds[selectedReel.reelId])}
        onPlayerError={setReelPlayerError}
        canShareToCoach={Boolean(selectedReel && user?.id === selectedReel.author.id)}
        onShareToCoach={(reel) => {
          setShareReel(reel);
          closeReelPlayer();
        }}
        onOpenComments={(reel) => {
          openComments({
            type: 'reel',
            id: reel.reelId,
            title: reel.title,
            subtitle: `By ${reel.author.name}`,
            commentCount: reel.commentCount,
          });
        }}
      />

      <ShareReelToCoachModal token={token} reel={shareReel} onClose={() => setShareReel(null)} />

      <CreatePostModal
        isOpen={isCreatePostModalOpen}
        loading={createPostLoading}
        error={createPostError}
        validationError={createPostValidationError}
        statusMessage={createPostStatusMessage}
        uploadProgressPercent={createPostUploadProgressPercent}
        content={newPostContent}
        selectedImageFiles={selectedPostImageFiles}
        selectedImagePreviewUrls={selectedPostImagePreviewUrls}
        textareaRef={createPostTextareaRef}
        fileInputRef={createPostFileInputRef}
        onContentChange={setNewPostContent}
        onClose={closeCreatePostModal}
        onBrowseImage={openCreatePostFileBrowser}
        onRemoveImage={removeSelectedPostImage}
        onImageChange={handleSelectedPostImageChange}
        onSubmit={handleCreatePostSubmit}
      />

      <RepostPostModal
        isOpen={isRepostModalOpen}
        targetPost={repostTargetPost}
        message={repostMessage}
        loading={repostLoading}
        error={repostError}
        validationError={repostValidationError}
        textareaRef={repostTextareaRef}
        failedImages={failedImages}
        onImageError={handleImageError}
        onMessageChange={setRepostMessage}
        onClose={closeRepostModal}
        onSubmit={handleRepostSubmit}
      />

      <CommentsModal
        target={selectedTarget}
        isOpen={isCommentsModalOpen}
        comments={comments}
        loading={commentsLoading}
        error={commentsError}
        draft={commentDraft}
        validationError={commentValidationError}
        submitError={commentSubmitError}
        submitting={submittingComment}
        deletingCommentIds={deletingCommentIds}
        currentUserId={user?.id}
        onDraftChange={setCommentDraft}
        onRetry={retryLoadingComments}
        onClose={closeComments}
        onSubmit={() => {
          void submitComment();
        }}
        onDeleteComment={(commentId) => {
          void deleteComment(commentId);
        }}
      />
    </div>
  );
};

export default Home;
